import { useCallback, useEffect, useState } from 'react';
import { bridgeApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Check, X, CircleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useHasTill } from '../../contexts/SiteContext';
import { usePageTitle } from '../../components/Layout';

/**
 * Getting a venue live — the fifteen things, and which of them is stopping you.
 *
 * The list has existed since Bridge phase 1 and has never had a screen, so the importers
 * have been ticking items off a document nobody could read. It matters now because the next
 * quarter is ten onboardings: without this the process lives in somebody's head, and the
 * answer to "what is left at the Didsbury site" is a phone call.
 *
 * Two things shape the screen:
 *
 * **Blocked is the interesting state, not done.** A list of ticks tells you nothing you did
 * not already know. "Card terminal — waiting on the acquirer since Tuesday" is the sentence
 * somebody acts on, so blocked items are lifted to the top and carry their reason, and the
 * progress line names what is in the way rather than only counting.
 *
 * **Two of the fifteen are not required.** Historical sales and scheduled reports are good
 * to have and no reason to hold an opening. They are counted separately so the headline is
 * honest: thirteen is the number that decides whether a venue can trade.
 */

//: The order an install actually happens in, which is the order the seed numbers them.
const GROUPS = [
  { key: 'data', label: 'Their data', hint: 'What the venue already has, brought in' },
  { key: 'staff', label: 'The team', hint: 'Who works there, and what they can do' },
  { key: 'operations', label: 'The floor', hint: 'Set up for service' },
  { key: 'hardware', label: 'The hardware', hint: 'The Pi, the printer, the card terminal' },
  { key: 'reporting', label: 'Reporting', hint: 'Not needed to open the doors' },
];

// Who the server says ticked an item it can see is done (`routes/bridge/checklist.SEEN`).
const SEEN = "Verlo, from what's set up";

// The good-to-have items as something to do, for the ready note.
const TODO = {
  historical_imported: "import last year's sales",
  reports_configured: 'schedule your reports',
};
const todo = (i) => TODO[i.key] || i.label.toLowerCase();
const andList = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const GoLivePage = () => {
  usePageTitle('Getting live');   // the top line has the title: no second one below
  const { hasPermission } = useAuth();
  const hasTill = useHasTill();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState('');
  const [blocking, setBlocking] = useState('');     // the key whose reason is being typed
  const [reason, setReason] = useState('');

  //: Ticking is `bridge.configure`, which a manager does not hold. They can still read the
  //: list — knowing what is outstanding is most of its value — so the controls go
  //: read-only rather than the page being hidden or every click answering 403.
  const mayEdit = hasPermission('bridge.configure');

  const load = useCallback(() => bridgeApi.getChecklist()
    .then(({ data }) => setItems(data || []))
    .catch(() => { setItems([]); toast.error('Could not load the checklist'); }), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (key, body) => {
    setBusy(key);
    try {
      const { data } = await bridgeApi.updateChecklistItem(key, body);
      setItems((prev) => prev.map((i) => (i.key === key ? data : i)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't save");
    } finally {
      setBusy('');
    }
  };

  const saveBlock = async (key) => {
    if (!reason.trim()) return;
    await patch(key, { blocked: true, blocked_reason: reason.trim() });
    setBlocking('');
    setReason('');
  };

  if (!items) return <Skeleton className="h-96 w-full" />;

  // A back-office venue was never sold a Pi, a printer or a card terminal, so three of the
  // fifteen can never be ticked there. They are set aside rather than left outstanding —
  // three permanently red rows is how a product teaches somebody it is broken. The
  // checklist is org-wide and the plan is per-site, so this follows the site in the header
  // and only ever softens: on "all sites", or at a venue with a floor, nothing is hidden.
  const setAside = (i) => !hasTill && i.category === 'hardware';

  const required = items.filter((i) => i.required_for_go_live && !setAside(i));
  const done = required.filter((i) => i.completed);
  const blocked = items.filter((i) => i.blocked && !setAside(i));
  const ready = required.length > 0 && done.length === required.length;
  // The good-to-have ones still not done, named, so the ready note says what's left, or that
  // nothing is (Oct 2026: it said "the two reporting items" whatever was ticked).
  const stillWorth = items.filter((i) => !i.required_for_go_live && !i.completed && !setAside(i));

  // A function that draws a row, not a component declared in here: a component made anew
  // on every render is a different component each time, so React threw the whole list away
  // and rebuilt it on every keystroke in "What is in the way?", box and caret included.
  const row = (item) => {
    const aside = setAside(item);
    // Ticked because Verlo can see it's done (the backend's `seen_done`): there's nothing to
    // untick, so the box isn't a button (Chris's review M6).
    const seen = item.completed && item.completed_by === SEEN;
    return (
      <div key={item.key} className={`flex items-start gap-3 border-t border-hairline px-5 py-3 ${aside ? 'opacity-55' : ''}`}
           data-testid={`golive-item-${item.key}`}>
        <button
          type="button"
          onClick={() => patch(item.key, { completed: !item.completed })}
          disabled={!mayEdit || aside || seen || busy === item.key}
          aria-pressed={item.completed}
          aria-label={seen ? `${item.label}: done, seen in Verlo` : item.completed ? `Mark ${item.label} not done` : `Mark ${item.label} done`}
          className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center border-[1.5px] ${
            item.completed ? 'border-verlo bg-verlo text-white'
              : item.blocked ? 'border-signal-warn-line bg-signal-warn-tint'
              : 'border-hairline-strong bg-surface'} ${mayEdit && !aside ? '' : 'cursor-default'}`}
        >
          {item.completed ? <Check size={12} /> : item.blocked ? <X size={11} className="text-signal-warn" /> : null}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className={`text-[13.5px] font-medium ${item.completed ? 'text-ink-soft line-through' : 'text-ink'}`}>
              {item.label}
            </span>
            {aside && <span className="text-[11.5px] text-ink-faint">not needed without a till</span>}
            {!item.required_for_go_live && !aside && (
              <span className="text-[11.5px] text-ink-faint">not needed to open</span>
            )}
          </div>
          <p className="mt-0.5 text-[12.5px] leading-snug text-ink-soft">{item.description}</p>

          {item.blocked && item.blocked_reason && (
            <p className="mt-1 text-[12.5px] font-medium text-signal-warn" data-testid={`golive-blocked-${item.key}`}>
              Blocked: {item.blocked_reason}
            </p>
          )}

          {item.completed && item.completed_by && (
            <p className="mt-0.5 text-[11.5px] text-ink-faint">
              {item.completed_by}{when(item.completed_at) ? ` · ${when(item.completed_at)}` : ''}
            </p>
          )}

          {blocking === item.key && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') saveBlock(item.key); }}
                     placeholder="What is in the way?" className="h-8 max-w-xs text-[13px]"
                     data-testid={`golive-reason-${item.key}`} />
              <Button size="sm" onClick={() => saveBlock(item.key)} disabled={!reason.trim()}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setBlocking(''); setReason(''); }}>Cancel</Button>
            </div>
          )}
        </div>

        {mayEdit && !aside && (!item.completed || seen) && blocking !== item.key && (
          <button type="button"
                  onClick={() => (item.blocked
                    ? patch(item.key, { blocked: false })
                    : (setBlocking(item.key), setReason('')))}
                  className="shrink-0 text-[12px] text-ink-faint underline-offset-2 hover:text-ink hover:underline"
                  data-testid={`golive-block-${item.key}`}>
            {item.blocked ? 'Unblock' : 'Mark as blocked'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="go-live-page">
      <div className="space-y-1">
        <p className="max-w-2xl text-[14px] text-ink-soft">
          The things a venue needs before it can trade on Verlo. Some tick themselves as you
          do them: importing a menu ticks its own box.
        </p>
      </div>

      {/* Where it stands, and what is in the way. */}
      <div className="rounded-lg border border-hairline bg-surface p-5 space-y-3" data-testid="golive-progress">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-[15px] font-bold text-ink">
            {done.length} of {required.length} ready
          </h2>
          {!mayEdit && <span className="text-[12.5px] text-ink-faint">You can see this list but not change it</span>}
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          <div className="h-full bg-verlo transition-[width] duration-300"
               style={{ width: `${required.length ? (done.length / required.length) * 100 : 0}%` }} />
        </div>

        {ready && blocked.length === 0 && (
          <Notice tone="ok" word="Ready" title="Ready to open" data-testid="golive-ready">
            {stillWorth.length
              ? `Still worth doing when you've time: ${andList(stillWorth.map(todo))}.`
              : "Everything's done."}
          </Notice>
        )}

        {blocked.length > 0 && (
          <Notice tone="warn" word="In the way"
                  title={`${blocked.length} ${blocked.length === 1 ? 'thing is' : 'things are'} blocked`}
                  className="text-[13px]">
            <ul className="space-y-0.5">
              {blocked.map((b) => (
                <li key={b.key}><b>{b.label}</b>{b.blocked_reason ? `: ${b.blocked_reason}` : ''}</li>
              ))}
            </ul>
          </Notice>
        )}
      </div>

      {GROUPS.map((g) => {
        const rows = items.filter((i) => i.category === g.key);
        if (!rows.length) return null;
        const groupDone = rows.filter((i) => i.completed).length;
        return (
          <div key={g.key} className="rounded-lg border border-hairline bg-surface" data-testid={`golive-group-${g.key}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-3">
              <h2 className="font-heading text-[15px] font-bold text-ink">{g.label}</h2>
              <span className="text-[12.5px] text-ink-faint">
                {g.hint} · {groupDone} of {rows.length}
              </span>
            </div>
            {rows.map(row)}
          </div>
        );
      })}

      {items.length === 0 && (
        <Notice tone="info" word="Empty" title="No checklist yet">
          It is created the first time somebody opens this page. Reload if this persists.
        </Notice>
      )}

      <p className="flex items-start gap-1.5 text-[12.5px] text-ink-faint">
        <CircleAlert size={13} className="mt-0.5 shrink-0" />
        This list is the organisation&rsquo;s, not one venue&rsquo;s. With more than one site
        it tracks the estate rather than each opening separately.
      </p>
    </div>
  );
};

export default GoLivePage;
